import React, { useState, useEffect, useMemo } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Button,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  Badge,
  CircularProgress,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  TextField,
  InputAdornment,
  Paper
} from "@mui/material";
import {
  Person,
  Edit,
  CheckCircle,
  Campaign as CampaignIcon,
  NoteAdd as NoteIcon,
  Room as RoomIcon,
  SupervisorAccount as SupervisorIcon,
  CalendarMonth as CalendarIcon,
  Email as EmailIcon,
  LocalOffer as TagIcon,
  Search as SearchIcon,
  PersonAddAlt as ConvertIcon,
  ChatBubbleOutline as ChatIcon,
  AddCircleOutline as AddClientIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import client from "../../../API";
import { renderAvatar } from "../dashboardUtils";
import ClientPopups from "./components/clientpopups";
import ChatModal from "../../Dashboard/Users/chatmodal";
import useAuth from "../../../Hooks/use-auth";

/* ───────── helpers ───────── */
const categories    = ["All", "Active", "Warm", "Cold", "Ghost"];
const categoryColors = {
  Active:    "success.main",
  Warm:      "warning.main",
  Cold:      "error.main",
  Ghost:     "grey.500",
  Prospect:  "info.main",
  All:       "divider",
};
const toTagArray = (tags) =>
  Array.isArray(tags)
    ? tags
    : typeof tags === "string"
        ? tags.split(",").map((t) => t.trim()).filter(Boolean)
        : [];

/* give every client a sortable “rank” — notes (rank 1) sit just after Active */
const rankOf = (cli, cat) => {
  if (cat === "Active") return 0;
  if (cli.note_count > 0) return 1;
  return ({ Warm:2, Cold:3, Ghost:4, Prospect:5, All:6 }[cat] ?? 6);
};

export default function ClientsView() {
  const nav   = useNavigate();
  const theme = useTheme();
  const { getCurrrentUser } = useAuth();

  /* ───────── data ───────── */
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  /* ───────── chat state ───────── */
  const [chatOpen, setChatOpen] = useState(false);
  const [targetClientId, setTargetClientId] = useState(null);

  /* ───────── ui state ───────── */
  const [tab,              setTab]              = useState(0);
  const [popup,            setPopup]            = useState({ type: null, client: null });
  const [tagFilter,        setTagFilter]        = useState([]);
  const [allTags,          setAllTags]          = useState([]);
  const [search,           setSearch]           = useState("");
  const [supervisorFilter, setSupervisorFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");

  /* ───────── fetch ───────── */
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const resp = await client.users.fetchAllClients();
        const arr  = Array.isArray(resp?.clients) ? resp.clients : [];
        setClients(
          arr.map((c) => ({
            ...c,
            id: c?.client_id ?? c?.id,
            tags: toTagArray(c.tags),
          }))
        );
      } catch (e) { setError(e); }
      finally     { setLoading(false); }
    })();
  }, []);

  /* derive unique tags */
  useEffect(() => {
    const u = new Set();
    clients.forEach((c) => c.tags.forEach((t) => u.add(t)));
    setAllTags([...u].sort((a, b) => a.localeCompare(b)));
  }, [clients]);

  /* supervisors list */
  const supervisors = useMemo(() => {
    const map = {};
    clients.forEach((c) => {
      if (c.supervisor_id) map[c.supervisor_id] = `Supervisor #${c.supervisor_id}`;
    });
    return Object.entries(map).map(([id, name]) => ({ id, name }));
  }, [clients]);

  /* categorisation */
  const now       = new Date();
  const daysSince = (d) => (now - new Date(d)) / 8.64e7;
  const categorize = (c) => {
    if (c.entity_type === "user") return "Prospect";
    const maps = c?.mapped_campaigns || [];
    if (!maps.length) return "Ghost";
    if (maps.some((m) => m.status === "Launched")) return "Active";
    if (maps.some((m) => daysSince(m.date_key) <= 14)) return "Warm";
    return "Cold";
  };

  /* filter + sort */
  const filtered = useMemo(() => {
    let list = [...clients];
    if (supervisorFilter) {
      list = list.filter((c) => String(c.supervisor_id) === supervisorFilter);
    }
    if (phoneFilter) {
      if (phoneFilter === 'available') list = list.filter((c) => c.phone_number);
      else if (phoneFilter === 'unavailable') list = list.filter((c) => !c.phone_number);
    }
    if (tab) list = list.filter((c) => categorize(c) === categories[tab]);
    if (tagFilter.length) list = list.filter((c) => c.tags.some((t) => tagFilter.includes(t)));
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((c) => {
                const hitInTags =
                  Array.isArray(c.tags) &&
                  c.tags.some((t) => t.toLowerCase().includes(q));
        
                return (
                  hitInTags ||
                  c.full_name?.toLowerCase().includes(q) ||
                  c.company?.toLowerCase().includes(q) ||
                  c.location?.toLowerCase().includes(q)
                );
              });
    }
    return list.sort((a, b) => {
      const ra = rankOf(a, categorize(a));
      const rb = rankOf(b, categorize(b));
      if (ra !== rb) return ra - rb;
      if (a.is_connected !== b.is_connected) return a.is_connected ? -1 : 1;
      return a.full_name.localeCompare(b.full_name);
    });
  }, [clients, tab, tagFilter, search, supervisorFilter, phoneFilter]);


  /* misc helpers */
  const jumpToCampaignSearch = (email) =>
    nav(`/search?client=${encodeURIComponent(email)}`);

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day:   "numeric",
      year:  "numeric",
    });

  const borderColorOf = (cli) => categoryColors[categorize(cli)];

  /* convert prospect → client */
  const convertToClient = async (cli) => {
    try {
      const res = await client.users.createClientFromUser({
        user_id: cli.user_id,
        tags:    cli.tags,
      });
      setClients((list) =>
        list.map((c) =>
          c.id === cli.id
            ? { ...res, id: res?.client_id, tags: toTagArray(res.tags) }
            : c
        )
      );
    } catch (err) {
      console.error(err);
      alert("Failed to create client");
    }
  };
const buildClientDraftFromUser = (user) => ({
  /* carry over the user’s details you want to pre-seed */
  full_name   : user.full_name,
  email       : user.email,
  location    : user.location,
  phone_number: user.phone_number || "",
  tags        : user.tags,
  /* pass the user_id so your modal can later POST /convert */
  user_id     : user.user_id,
  entity_type : "client",          // so the form sees it as a client draft
});

  /* ───────── render ───────── */
  if (loading) return <CircularProgress sx={{ m: 4 }} />;
  if (error)
    return (
      <Typography color="error" sx={{ m: 4 }}>
        Error: {error.message || "Failed to load clients"}
      </Typography>
    );

  return (
    <>
       {/* Filter Bar */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, company, or location"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon/></InputAdornment>
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Supervisor</InputLabel>
              <Select value={supervisorFilter} label="Supervisor" onChange={(e) => setSupervisorFilter(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                {supervisors.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Phone</InputLabel>
              <Select value={phoneFilter} label="Phone" onChange={(e) => setPhoneFilter(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                <MenuItem value="available">Has Phone</MenuItem>
                <MenuItem value="unavailable">No Phone</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Tags</InputLabel>
              <Select multiple value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} input={<OutlinedInput label="Tags"/>}>
                {allTags.map((t) => (
                  <MenuItem key={t} value={t}><Checkbox checked={tagFilter.includes(t)}/>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Button variant="contained" onClick={() => setPopup({ type: 'add' })}>Add Client</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, px: 3 }}
        variant="scrollable"
        scrollButtons
        allowScrollButtonsMobile
      >
        {categories.map((l) => (
          <Tab key={l} label={l} />
        ))}
      </Tabs>

      {/* cards */}
      <Grid container spacing={3} sx={{ px: 3, pb: 6 }}>
        {filtered.map((cli) => {
          const cat   = categorize(cli);
          const maps  = cli?.mapped_campaigns || [];
          const totalProps   = maps.length;
          const launchedAmt  = maps.filter((m) => m.status === "Launched").reduce((s, m) => s + (m.amount || 0), 0);
          const completedAmt = maps.filter((m) => m.status === "Completed").reduce((s, m) => s + (m.amount || 0), 0);
          const draftAmt     = maps.filter((m) => m.status === "Draft").reduce((s, m) => s + (m.amount || 0), 0);
          const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(cli?.email || "")}`;

          return (
            <Grid key={cli.id} item xs={12} sm={6} md={4} lg={3}>
              <Card
                sx={{
                  height: "100%",
                  position: "relative",
                  cursor: cli.entity_type === "user" ? "default" : "pointer",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
                  borderRadius: 3,
                  border: 2,
                  borderColor: borderColorOf(cli),
                  display: "flex",
                  flexDirection: "column",
                }}
                onClick={() =>
                  cli.entity_type === "user"
                    ? null
                    : setPopup({ type: "campaigns", client: cli })
                }
              >
                {cli?.is_connected && cli.entity_type !== "user" && (
                  <CheckCircle
                    sx={{
                      position: "absolute",
                      top: 8,
                      left: 8,
                      color: "success.main",
                      fontSize: 20,
                    }}
                  />
                )}

                {/* action buttons */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    display: "flex",
                    gap: 0.75,
                  }}
                >
                  {cli.entity_type === "user" ? (
                    <Tooltip title="Add as Client (pre-filled)">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPopup({
                          type  : "add",                      // ← your ClientPopups ‘add’ form
                          client: buildClientDraftFromUser(cli)
                        });
                      }}
                    >
                      <AddClientIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  ) : (
                    <>                        
                      {cli.entity_type !== "user" && (
                        <>
                          <Tooltip title="Send email">
                            <IconButton
                              size="small"
                              component="a"
                              href={gmailLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Create / view campaigns">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); jumpToCampaignSearch(cli.email); }}
                            >
                              <CampaignIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="View notes">
                        <Badge badgeContent={cli.note_count || 0} max={99} color="secondary">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setPopup({ type: "notes", client: cli }); }}
                          >
                            <NoteIcon fontSize="small" />
                          </IconButton>
                        </Badge>
                      </Tooltip>

                      {cli.is_connected && cli.entity_type !== "user" && (
                        <Tooltip title="Chat">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setTargetClientId(cli.id); setChatOpen(true); }}
                          >
                            <ChatIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setPopup({ type: "edit", client: cli }); }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>

                {/* content */}
                <CardContent
                  sx={{
                    textAlign: "center",
                    pt: 5,
                    pb: 3,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  {renderAvatar(cli, "clients")}

                  <Typography
                    variant="h6"
                    fontWeight={600}
                    gutterBottom
                    sx={{ mt: 1, [theme.breakpoints.down("sm")]: { fontSize: "1rem" } }}
                  >
                    {cli.full_name}
                  </Typography>

                  <Chip
                    icon={<Person />}
                    label={cat}
                    size="small"
                    sx={{ borderColor: borderColorOf(cli), color: borderColorOf(cli), mb: 1 }}
                  />

                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                      justifyContent: "center",
                      my: 1,
                    }}
                  >
                    {cli.supervisor_id && cli.entity_type !== "user" && (
                      <Chip
                        icon={<SupervisorIcon />}
                        label={`Supervisor #${cli.supervisor_id}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {cli.location && (
                      <Chip
                        icon={<RoomIcon />}
                        label={cli.location}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {cli.tags.length > 0 && (
                    <Box
                      sx={{
                        width: "100%",
                        mt: 1,
                        display: "flex",
                        gap: 0.5,
                        flexWrap: "wrap",
                        justifyContent: "center",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {cli.tags.slice(0, 4).map((t) => (
                        <Chip
                          key={t}
                          icon={<TagIcon fontSize="inherit" />}
                          label={t}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {cli.tags.length > 4 && (
                        <Chip
                          label={`+${cli.tags.length - 4}`} 
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  )}

                  {cli.date_created && cli.entity_type !== "user" && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}
                    >
                      <CalendarIcon fontSize="small" />
                      {fmtDate(cli.date_created)}
                    </Typography>
                  )}

                  {cli.bio && cli.entity_type !== "user" && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ maxHeight: 80, overflow: "hidden", textOverflow: "ellipsis", mb: 1, px: 1 }}
                    >
                      {cli.bio}
                    </Typography>
                  )}

                  {cli.company && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ maxHeight: 80, overflow: "hidden", textOverflow: "ellipsis", mb: 1, px: 1 }}
                    >
                      {cli.company}
                    </Typography>
                  )}

                  {cli.entity_type !== "user" && (
                    <>
                      <Typography variant="body2" gutterBottom>
                        {totalProps} proposal{totalProps !== 1 && "s"}
                      </Typography>

                      <Typography variant="caption" display="block">
                        Launched:  ${launchedAmt.toLocaleString()}<br />
                        Completed: ${completedAmt.toLocaleString()}<br />
                        Drafts:    ${draftAmt.toLocaleString()}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* dialogs */}
      <ClientPopups
        popup={popup}
        setPopup={setPopup}
        refreshClients={async () => {
          try {
            const resp = await client.users.fetchAllClients();
            const arr  = Array.isArray(resp?.clients) ? resp.clients : [];
            setClients(
              arr.map((c) => ({
                ...c,
                id: c?.client_id ?? c.id,
                tags: toTagArray(c.tags),
              }))
            );
          } catch (e) {
            console.error(e);
          }
        }}
        pushClientToState={(updated) =>
          setClients((list) =>
            list.map((c) =>
              c.id === (updated?.client_id ?? updated?.id)
                ? { ...updated, id: updated?.client_id, tags: toTagArray(updated.tags) }
                : c
            )
          )
        }
      />

      {/* Chat Modal */}
      <ChatModal
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        recipientId={targetClientId}
      />
    </>
  );
}
